import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { formatDateTime } from '../format';
import type { JournalEntry, JournalPiece } from '../../shared/editorial';
import { SafeMarkdown } from '../editorial/markdown';

function JournalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="journal-shell">
      <header className="journal-header">
        <Link to="/journal" className="journal-wordmark">Tentacle Journal</Link>
        <nav aria-label="Journal navigation">
          <Link to="/">Studio</Link>
          <Link to="/login?next=/dashboard">Workspace</Link>
        </nav>
      </header>
      <main>{children}</main>
    </div>
  );
}

export function JournalIndex() {
  const [items, setItems] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<JournalEntry[] | { items: JournalEntry[] }>('/journal')
      .then((response) => setItems(Array.isArray(response) ? response : response.items))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <JournalShell>
      <section className="journal-intro">
        <h1>Journal</h1>
        <p>Bulletins from the Tentacle collective.</p>
      </section>
      {error ? <p className="journal-error" role="alert">{error}</p> : null}
      {loading ? <p className="journal-empty">Loading journal…</p> : null}
      {!loading && items.length === 0 ? <p className="journal-empty">No pieces have been published here yet.</p> : null}
      <ol className="journal-index">
        {items.map((piece, index) => (
          <li key={piece.id}>
            <span className="journal-index-number">{String(index + 1).padStart(2, '0')}</span>
            <div>
              <h2><Link to={`/journal/${piece.id}`}>{piece.title || 'Untitled'}</Link></h2>
              <p>{piece.bodyMd.replace(/\s+/g, ' ').trim().slice(0, 220)}{piece.bodyMd.length > 220 ? '…' : ''}</p>
            </div>
            <time>{formatDateTime(piece.publishedAt)}</time>
          </li>
        ))}
      </ol>
    </JournalShell>
  );
}

export function JournalDetail() {
  const { id } = useParams();
  const [piece, setPiece] = useState<JournalPiece | null>(null);
  const [publication, setPublication] = useState<JournalEntry['publication'] | null>(null);
  const [profile, setProfile] = useState<JournalEntry['profile'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPiece(null);
    setPublication(null);
    setProfile(null);
    api
      .get<{
        piece: JournalPiece;
        publication: JournalEntry['publication'];
        profile: JournalEntry['profile'];
      }>(`/journal/${id}`)
      .then((response) => {
        if (cancelled) return;
        // The public response is intentionally the only document rendered here.
        // It contains no private sources, rationale, lessons, or work records.
        setPiece(response.piece);
        setPublication(response.publication);
        setProfile(response.profile);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <JournalShell>
      {loading ? <p className="journal-empty">Loading piece…</p> : null}
      {error ? <p className="journal-error" role="alert">{error}</p> : null}
      {!loading && piece ? (
        <article className="journal-article">
          <header>
            <Link to="/journal">Journal</Link>
            <h1>{piece.title || 'Untitled'}</h1>
            <p>
              {publication?.name ?? 'Tentacle'}
              {profile?.name ? ` / ${profile.name}` : ''}
              {' / '}
              <time>{formatDateTime(piece.publishedAt)}</time>
            </p>
          </header>
          <div className="journal-body"><SafeMarkdown markdown={piece.bodyMd} /></div>
        </article>
      ) : null}
    </JournalShell>
  );
}
